// src/components/dashboard/StageChips.tsx
//
// The row that answers "where is my campaign" without reading a single row of
// the table. This replaces the old status dropdown, which offered three values
// the API never wrote and omitted the one almost every email ends up in.

import React from 'react';
import { EMAIL_STAGES, EmailStage, STAGE_DISPLAY, StageCounts } from '../../types/stage';

interface StageChipsProps {
  counts: StageCounts | null;
  selected: string | null;
  onSelect: (stage: string | null) => void;
  /** Stages to offer. Defaults to all of them. */
  stages?: EmailStage[];
  heldSelected: boolean;
  onToggleHeld: () => void;
}

const TONE_ACTIVE: Record<string, string> = {
  neutral: 'bg-gray-700 text-white border-gray-700',
  progress: 'bg-blue-600 text-white border-blue-600',
  good: 'bg-green-600 text-white border-green-600',
  warn: 'bg-amber-600 text-white border-amber-600',
  bad: 'bg-red-600 text-white border-red-600',
};

const TONE_IDLE: Record<string, string> = {
  neutral: 'bg-white text-gray-700 border-gray-300 hover:border-gray-400',
  progress: 'bg-white text-blue-700 border-blue-200 hover:border-blue-400',
  good: 'bg-white text-green-700 border-green-200 hover:border-green-400',
  warn: 'bg-white text-amber-700 border-amber-200 hover:border-amber-400',
  bad: 'bg-white text-red-700 border-red-200 hover:border-red-400',
};

const formatCount = (n: number) => n.toLocaleString();

const StageChips: React.FC<StageChipsProps> = ({
  counts,
  selected,
  onSelect,
  stages = [...EMAIL_STAGES],
  heldSelected,
  onToggleHeld,
}) => {
  if (!counts) {
    return (
      <div className="flex gap-2 mb-4" aria-hidden="true">
        {stages.slice(0, 5).map((s) => (
          <div key={s} className="h-9 w-28 bg-gray-200 rounded-full animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      <button
        onClick={() => onSelect(null)}
        className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-colors ${
          selected === null
            ? 'bg-gray-900 text-white border-gray-900'
            : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
        }`}
      >
        All <span className="tabular-nums opacity-80">{formatCount(counts.total)}</span>
      </button>

      {stages.map((stage) => {
        const display = STAGE_DISPLAY[stage];
        const count = counts.stages[stage] ?? 0;
        const isSelected = selected === stage;

        return (
          <button
            key={stage}
            onClick={() => onSelect(isSelected ? null : stage)}
            title={display.description}
            className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-colors ${
              isSelected ? TONE_ACTIVE[display.tone] : TONE_IDLE[display.tone]
            }`}
          >
            {display.label}{' '}
            <span className="tabular-nums opacity-80">{formatCount(count)}</span>
          </button>
        );
      })}

      {/* Held is derived from the clock rather than stored, so it sits apart from
          the stages proper — it is a refinement of Queued, not a stage. */}
      {counts.held > 0 && (
        <button
          onClick={onToggleHeld}
          title="Queued but not eligible yet: paused, scheduled for later, or inside a retry backoff."
          className={`px-3 py-1.5 text-sm font-medium rounded-full border border-dashed transition-colors ${
            heldSelected
              ? 'bg-amber-600 text-white border-amber-600'
              : 'bg-white text-amber-700 border-amber-300 hover:border-amber-500'
          }`}
        >
          Held <span className="tabular-nums opacity-80">{formatCount(counts.held)}</span>
        </button>
      )}
    </div>
  );
};

export default StageChips;
